import {
  useCallback,
  useRef,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { boardSwitcherSharedState } from '@trello/board-switcher';
import { useSharedState } from '@trello/shared-state';

import { OfferingLozenge } from 'app/src/components/OfferingLozenge';
import { BoardSwitcherTile } from './BoardSwitcherTile';
import { BoardSwitcherTiles } from './BoardSwitcherTiles';
import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { CreateBoardButton } from './CreateBoardButton';
import { CreateBoardTile } from './CreateBoardTile';
import { ExpandSection } from './ExpandSection';
import { isRealWorkspace } from './isRealWorkspace';
import { useIsGuestWorkspace } from './useIsGuestWorkspace';
import { useWorkspaceBoardsSectionPrefetchLazyQuery } from './WorkspaceBoardsSectionPrefetchQuery.generated';
import { WorkspaceMoreButton } from './WorkspaceMoreButton';

import styles from './WorkspaceBoards.less';

export const PREFETCH_DELAY = 150;

const WorkspaceDisplayName: FunctionComponent<{
  id: string;
  displayName?: string;
  isSelectedWorkspace: boolean;
}> = ({ id, displayName, isSelectedWorkspace }) => {
  if (isSelectedWorkspace) {
    return (
      <FormattedMessage
        id="templates.board_switcher.your-boards"
        defaultMessage="Your boards"
        description="Title of the boards section of a workspace on the board switcher"
      />
    );
  }

  // unknown should never happen but just in case it does
  return (
    displayName ?? (
      <FormattedMessage
        id="templates.board_switcher.unknown-workspace"
        defaultMessage="Unknown workspace"
        description="Title of an unknown workspace on the board switcher"
      />
    )
  );
};

interface WorkspaceBoardsProps {
  workspaceId: string;
  boardIds: string[];
  showActions?: boolean;
  showCreateBoardTile?: boolean;
}

export const WorkspaceBoards: FunctionComponent<WorkspaceBoardsProps> = ({
  workspaceId,
  boardIds,
  showActions = false,
  showCreateBoardTile = false,
}) => {
  const [{ selectedWorkspaceId, expandSectionsState }] = useSharedState(
    boardSwitcherSharedState,
  );

  const fragmentId = isRealWorkspace(workspaceId) ? workspaceId : null;

  const { data } = useBoardSwitcherWorkspaceFragment({
    from: { id: fragmentId },
    returnPartialData: true,
  });

  const isSelectedWorkspace = workspaceId === selectedWorkspaceId;

  const expandSectionId = isSelectedWorkspace
    ? `your-boards-${workspaceId}`
    : `boards-${workspaceId}`;

  const isExpandedByDefault = isSelectedWorkspace
    ? (expandSectionsState[expandSectionId] ?? true)
    : (expandSectionsState[expandSectionId] ?? false);

  const isGuestWorkspace = useIsGuestWorkspace(workspaceId);

  const shouldShowCreateBoardButton = isRealWorkspace(workspaceId);

  /**
   * When the user hovers over the workspace, prefetch the data for the workspace
   * (which includes the memberships) to avoid a loading state if they open the
   * ...more menu
   */
  const ref = useRef<number | null>(null);
  const [prefetchWorkspaceData] = useWorkspaceBoardsSectionPrefetchLazyQuery();

  /**
   * Only prefetch if the mouse stays hovered to avoid unnecessary requests if
   * they quickly swipe over the workspace. Only prefetch if the actions buttons
   * are shown
   */
  const onMouseEnter = useCallback(() => {
    if (!isGuestWorkspace && showActions) {
      ref.current = window.setTimeout(() => {
        prefetchWorkspaceData({
          variables: {
            id: workspaceId,
          },
        });
      }, PREFETCH_DELAY);
    }
  }, [prefetchWorkspaceData, workspaceId, isGuestWorkspace, showActions]);

  const onMouseLeave = useCallback(() => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
  }, []);

  if (boardIds.length === 0 && !showCreateBoardTile) {
    return null;
  }

  let actions: ReactNode = null;

  if (!isGuestWorkspace && showActions) {
    actions = (
      <>
        {shouldShowCreateBoardButton && (
          <CreateBoardButton workspaceId={workspaceId} />
        )}
        <WorkspaceMoreButton workspaceId={workspaceId} />
      </>
    );
  }

  if (isGuestWorkspace && !isSelectedWorkspace) {
    actions = (
      <OfferingLozenge offering="guest" className={styles.offeringLozenge} />
    );
  }

  return (
    <ExpandSection
      key={expandSectionId}
      id={expandSectionId}
      title={
        <WorkspaceDisplayName
          id={workspaceId}
          displayName={data?.displayName}
          isSelectedWorkspace={isSelectedWorkspace}
        />
      }
      workspaceId={workspaceId}
      isExpandedByDefault={isExpandedByDefault}
      actions={actions}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <BoardSwitcherTiles>
        {boardIds.map((boardId) => (
          <BoardSwitcherTile
            key={boardId}
            boardId={boardId}
            section="workspace"
          />
        ))}
        {showCreateBoardTile && <CreateBoardTile workspaceId={workspaceId} />}
      </BoardSwitcherTiles>
    </ExpandSection>
  );
};
